import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { StringHelper, ObjectHelper } from '../helpers';
import { Permissao } from '../helpers/PermissaoHelper';
import { useSearchParams } from './useSearchParams';
import { useSelector } from 'react-redux';

type PrefilterParams = {
    id: string | null;
    id_biblioteca: string | null;
    id_colecao: string | null;
    tipo_obra: string | null;
    cr: string | null;
};

interface Atividade {
    cod_atividade: string | number;
}

export const usePrefiterParams = () => {
    const usesUrlPrefilters = useSelector(
        (state: any) =>
            state.pageConfig.atividades?.some(
                (atividade: Atividade) =>
                    Number(atividade.cod_atividade) === 144,
            ) ?? false,
    );

    const { search } = useLocation();
    const { clearSearchParams } = useSearchParams();
    const PREFILTERS_STORAGE_KEY = 'pergamum-prefilters';
    const prefilterKeys: (keyof PrefilterParams)[] = [
        'id',
        'id_biblioteca',
        'cr',
        'id_colecao',
        'tipo_obra',
    ];

    const getPrefiltersFromSearch = (currentSearch: string): PrefilterParams => {
        const parsedQuery = new URLSearchParams(currentSearch);
        const params: PrefilterParams = {
            id: parsedQuery.get('id'),
            id_biblioteca: StringHelper.getWithCommaAtTheEnd(
                parsedQuery.get('id_biblioteca'),
            ),
            cr: parsedQuery.get('cr'),
            id_colecao: StringHelper.getWithCommaAtTheEnd(
                parsedQuery.get('id_colecao'),
            ),
            tipo_obra: StringHelper.getWithCommaAtTheEnd(
                parsedQuery.get('tipo_obra'),
            ),
        };

        Object.keys(params).forEach(key => {
            const value = params[key as keyof PrefilterParams];
            if (typeof value === 'string') {
                params[key as keyof PrefilterParams] = decodeURIComponent(
                    value,
                );
            }
        });

        return params;
    };

    const initPrefilters = () => {
        if (Permissao.tem(144)) return;

        const currentParams = getAll();
        const parsedQuery = new URLSearchParams(search);
        let newParams: PrefilterParams = {
            id: null,
            id_biblioteca: null,
            cr: null,
            id_colecao: null,
            tipo_obra: null,
        };
        if (parsedQuery.has('id'))
            newParams.id = parsedQuery.get('id');
        if (parsedQuery.has('id_biblioteca'))
            newParams.id_biblioteca = StringHelper.getWithCommaAtTheEnd(
                parsedQuery.get('id_biblioteca'),
            );
        if (parsedQuery.has('cr'))
            newParams.cr = parsedQuery.get('cr');
        if (parsedQuery.has('id_colecao'))
            newParams.id_colecao = StringHelper.getWithCommaAtTheEnd(
                parsedQuery.get('id_colecao'),
            );
        if (parsedQuery.has('tipo_obra'))
            newParams.tipo_obra = StringHelper.getWithCommaAtTheEnd(
                parsedQuery.get('tipo_obra'),
            );

        if (
            !ObjectHelper.areAllFieldsNull(newParams) &&
            !ObjectHelper.compareObjects(newParams, currentParams)
        )
            localStorage.setItem(
                PREFILTERS_STORAGE_KEY,
                JSON.stringify(newParams),
            );

        prefilterKeys.forEach(key => parsedQuery.delete(key));
        clearSearchParams(parsedQuery);
    };

    const getAll = useCallback((): PrefilterParams => {
        if (usesUrlPrefilters) return getPrefiltersFromSearch(search);

        let params: PrefilterParams = {
            id: null,
            id_biblioteca: null,
            cr: null,
            id_colecao: null,
            tipo_obra: null,
        };
        const fromStorageRaw = localStorage.getItem(PREFILTERS_STORAGE_KEY);
        if (fromStorageRaw)
            params = JSON.parse(fromStorageRaw) as PrefilterParams;
        Object.keys(params).forEach(key => {
            const value = params[key as keyof PrefilterParams];
            if (typeof value === 'string') {
                params[key as keyof PrefilterParams] = decodeURIComponent(
                    value,
                );
            }
        });

        return params;
    }, [search, usesUrlPrefilters]);

    const remove = (key: keyof PrefilterParams) => {
        if (usesUrlPrefilters) {
            const params = new URLSearchParams(search);
            params.delete(key);
            clearSearchParams(params);
            return;
        }

        const all = getAll();
        all[key] = null;
        localStorage.setItem(
            PREFILTERS_STORAGE_KEY,
            JSON.stringify(all),
        );
    }

    const clear = () => {
        if (usesUrlPrefilters) {
            const params = new URLSearchParams(search);
            prefilterKeys.forEach(key => params.delete(key));
            clearSearchParams(params);
            return;
        }

        localStorage.removeItem(PREFILTERS_STORAGE_KEY);
    }

    return { initPrefilters, getAll, remove, clear } as const;
};
